"use client";

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { categories } from "@/lib/size";

interface SizeChartModalProps {
  gender?: string | null;
  productSizeGuide?: Record<string, string>[] | null;
  onClose: () => void;
}

type SizeRow = { label: string } & Record<string, string>;

const TAB_LABELS: Record<string, string> = {
  SIZE_GUIDE_TOPS: "Tops & Shirts",
  SIZE_GUIDE_BOTTOMS: "Bottoms & Trousers",
};

function tabLabel(key: string) {
  return (
    TAB_LABELS[key] ??
    key
      .replace("SIZE_GUIDE_", "")
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase())
  );
}

function possessive(title: string) {
  return title.toLowerCase().endsWith("s") ? `${title}'` : `${title}'s`;
}

export default function SizeChartModal({
  gender,
  productSizeGuide,
  onClose,
}: SizeChartModalProps) {
  const wantedTitle = (gender || "women").trim().toLowerCase();
  const defaultCategory =
    categories.find((c) => c.title.toLowerCase() === wantedTitle) ??
    categories.find((c) => c.title === "Women")!;

  // ── User-selectable gender (Men / Women / Kids) ──────────────────────────
  const [activeGender, setActiveGender] = useState<string>(defaultCategory.title);

  const category =
    categories.find((c) => c.title === activeGender) ?? defaultCategory;

  const guideKeys = Object.keys(category).filter((k) =>
    k.startsWith("SIZE_GUIDE_")
  ) as (keyof typeof category)[];

  const hasProductGuide = !!(productSizeGuide && productSizeGuide.length);

  // ── User-selectable type (Tops / Bottoms) ────────────────────────────────
  const [activeType, setActiveType] = useState<string>(guideKeys[0] as string);

  // Reset the type tab to the first one whenever gender changes
  useEffect(() => {
    const keys = Object.keys(category).filter((k) => k.startsWith("SIZE_GUIDE_"));
    setActiveType(keys[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGender]);

  const activeRows: SizeRow[] = hasProductGuide
    ? (productSizeGuide as SizeRow[])
    : ((category[activeType as keyof typeof category] as unknown as SizeRow[]) ?? []);

  const columns = activeRows.length
    ? Object.keys(activeRows[0]).filter((k) => k !== "label")
    : [];

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Size chart"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#ece7df] sticky top-0 bg-white z-10">
          <div>
            <p className="font-['DM_Mono'] text-[10px] uppercase tracking-widest text-[#b8955a] mb-1">
              Modish Kreations
            </p>
            <h3 className="font-['Cormorant_Garamond'] italic text-[1.4rem] text-[#1a1a1a]">
              {possessive(category.title)} Size Guide
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close size chart"
            className="w-8 h-8 rounded-full bg-[#f5f0e8] flex items-center justify-center hover:bg-[#ede7db] transition-colors shrink-0"
          >
            <X size={14} className="text-[#666]" />
          </button>
        </div>

        <div className="p-6">
          {/* Gender selector — Men / Women / Kids */}
          {!hasProductGuide && (
            <div className="flex gap-2 mb-5">
              {categories.map((c) => {
                const active = activeGender === c.title;
                return (
                  <button
                    key={c.title}
                    onClick={() => setActiveGender(c.title)}
                    className={`flex-1 font-['DM_Mono'] text-[11px] tracking-[0.12em] uppercase font-bold py-2.5 rounded-full transition-colors ${
                      active
                        ? "bg-[#b8955a] text-white"
                        : "bg-[#faf7f2] text-[#555] border border-[#e0dbd2] hover:border-[#b8955a]"
                    }`}
                  >
                    {c.title}
                  </button>
                );
              })}
            </div>
          )}

          {/* Type selector — Tops / Bottoms */}
          {!hasProductGuide && guideKeys.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {guideKeys.map((key) => {
                const active = activeType === key;
                return (
                  <button
                    key={key as string}
                    onClick={() => setActiveType(key as string)}
                    className={`font-['DM_Mono'] text-[11px] tracking-[0.1em] uppercase font-bold px-5 py-2.5 rounded-full transition-colors ${
                      active
                        ? "bg-[#1a1a1a] text-white"
                        : "bg-white text-[#555] border border-[#e0dbd2] hover:border-[#1a1a1a]"
                    }`}
                  >
                    {tabLabel(key as string)}
                  </button>
                );
              })}
            </div>
          )}

          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-widest text-[#888] mb-3">
            {hasProductGuide
              ? "Product-specific measurements"
              : `${category.title} ${tabLabel(activeType).toLowerCase()} (inches)`}
          </p>

          <div className="rounded-xl overflow-hidden border border-[#e8e4dd]">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#f7f4ef] border-b border-[#e8e4dd]">
                  <th className="py-3 px-4 font-['DM_Mono'] text-[10px] uppercase tracking-wide text-[#888]">
                    Size
                  </th>
                  {columns.map((col) => (
                    <th
                      key={col}
                      className="py-3 px-4 font-['DM_Mono'] text-[10px] uppercase tracking-wide text-[#888] capitalize"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeRows.map((row, i) => (
                  <tr
                    key={row.label ?? i}
                    className={`border-b border-[#f0ebe3] last:border-b-0 ${
                      i % 2 === 1 ? "bg-[#faf8f5]" : "bg-white"
                    }`}
                  >
                    <td className="py-3 px-4 font-['Cormorant_Garamond'] italic text-[16px] font-bold text-[#b8955a]">
                      {row.label}
                    </td>
                    {columns.map((col) => (
                      <td
                        key={col}
                        className="py-3 px-4 font-['DM_Mono'] text-[12px] text-[#333]"
                      >
                        {row[col]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="font-['DM_Mono'] text-[10px] text-[#aaa] mt-4">
            All measurements in inches. For the best fit, compare against a
            similar garment you already own.
          </p>
        </div>
      </div>
    </div>
  );
}